const GENERIC_FONT_FAMILIES = new Set([
  'cursive',
  'emoji',
  'fangsong',
  'fantasy',
  'inherit',
  'initial',
  'math',
  'monospace',
  'revert',
  'sans-serif',
  'serif',
  'system-ui',
  'ui-monospace',
  'ui-sans-serif',
  'ui-serif',
  'unset',
]);

type ReportFontFaceRule = {
  type: number;
  cssText: string;
};

export type ReportFontStyleSheet = {
  href: string | null;
  readCssRules: () => readonly ReportFontFaceRule[];
};

type BuildReportThumbnailFontEmbedCssOptions = {
  styleSheets?: readonly ReportFontStyleSheet[];
  fetchText?: (url: string) => Promise<string>;
  fetchDataUrl?: (url: string) => Promise<string>;
};

const stripCssComments = (cssText: string): string =>
  cssText.replace(/\/\*[\s\S]*?\*\//g, '');

/**
 * Comparable font-family token. Quotes and surrounding space are removed.
 */
export const normalizeFontFamilyName = (font: string): string =>
  font
    .trim()
    .replace(/^['"]+|['"]+$/g, '')
    .toLowerCase();

type StyledElement = HTMLElement | SVGElement;

const isStyledElement = (node: Element): node is StyledElement =>
  node instanceof HTMLElement || node instanceof SVGElement;

const readDeclaredFontFamily = (element: StyledElement): string => {
  const svgFamily =
    element instanceof SVGElement ? element.getAttribute('font-family') : null;
  return (
    svgFamily ||
    element.style.fontFamily ||
    getComputedStyle(element).fontFamily
  );
};

/**
 * Font families painted inside `root`, excluding generics such as `sans-serif`.
 */
export const collectUsedFontFamilies = (root: HTMLElement): Set<string> => {
  const families = new Set<string>();

  const visit = (element: StyledElement) => {
    for (const part of readDeclaredFontFamily(element).split(',')) {
      const name = normalizeFontFamilyName(part);
      if (!name || GENERIC_FONT_FAMILIES.has(name)) continue;
      families.add(name);
    }
    for (const child of element.children) {
      if (!isStyledElement(child)) continue;
      visit(child);
    }
  };

  visit(root);
  return families;
};

/**
 * `@font-face` blocks in `cssText`. Comment text is ignored.
 */
export const parseFontFaceRules = (cssText: string): string[] =>
  stripCssComments(cssText).match(/@font-face\s*\{[^}]*\}/gi) ?? [];

/**
 * First `font-family` declared by an `@font-face` block.
 */
export const readFontFaceFamily = (fontFaceCss: string): string | undefined => {
  const match = fontFaceCss.match(/font-family:\s*([^;]+)/i);
  const listed = match?.[1]?.split(',')[0];
  if (!listed) return;
  const name = normalizeFontFamilyName(listed);
  if (!name) return;
  return name;
};

const pickEmbeddableFontUrl = (fontFaceCss: string): string | undefined => {
  const urls = [...fontFaceCss.matchAll(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi)]
    .map((match) => match[2] ?? '')
    .filter((url) => url.length > 0 && !url.startsWith('data:'));
  return urls.find((url) => url.toLowerCase().includes('woff2')) ?? urls[0];
};

const resolveAgainstStylesheet = (
  url: string,
  baseHref: string | null,
): string => {
  if (url.startsWith('data:') || /^https?:\/\//i.test(url)) return url;
  if (!baseHref) return url;
  return new URL(url, baseHref).href;
};

const replaceFontSource = (fontFaceCss: string, dataUrl: string): string => {
  const source = `src: url("${dataUrl}");`;
  if (/src\s*:/i.test(fontFaceCss)) {
    return fontFaceCss.replace(/src\s*:[^;]*;/i, () => source);
  }
  return fontFaceCss.replace(/\}$/, `${source}}`);
};

const readBlobAsDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Font file did not read as a data URL'));
        return;
      }
      resolve(reader.result);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('Failed to read font file'));
    };
    reader.readAsDataURL(blob);
  });

const defaultFetchText = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch font stylesheet');
  return response.text();
};

const defaultFetchDataUrl = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch font file');
  return readBlobAsDataUrl(await response.blob());
};

const defaultStyleSheets = (): ReportFontStyleSheet[] =>
  [...document.styleSheets].flatMap((sheet) => {
    if (!('cssRules' in sheet)) return [];
    return [
      {
        href: sheet.href,
        readCssRules: () => [...sheet.cssRules],
      },
    ];
  });

const readFacesFromSheet = async (
  sheet: ReportFontStyleSheet,
  fetchText: (url: string) => Promise<string>,
): Promise<{ href: string | null; faces: string[] }> => {
  try {
    const faces = sheet
      .readCssRules()
      .filter((rule) => rule.type === CSSRule.FONT_FACE_RULE)
      .map((rule) => rule.cssText);
    return { href: sheet.href, faces };
  } catch {
    // Google Fonts stylesheets are cross-origin, so cssRules throws SecurityError.
    if (!sheet.href) return { href: null, faces: [] };
    try {
      const cssText = await fetchText(sheet.href);
      return { href: sheet.href, faces: parseFontFaceRules(cssText) };
    } catch {
      return { href: sheet.href, faces: [] };
    }
  }
};

const embedFontFace = async (
  face: string,
  baseHref: string | null,
  fetchDataUrl: (url: string) => Promise<string>,
): Promise<string | undefined> => {
  const fontUrl = pickEmbeddableFontUrl(face);
  if (!fontUrl) return face;
  const resolved = resolveAgainstStylesheet(fontUrl, baseHref);
  if (resolved.startsWith('data:')) return face;
  try {
    const dataUrl = await fetchDataUrl(resolved);
    return replaceFontSource(face, dataUrl);
  } catch {
    return undefined;
  }
};

/**
 * `@font-face` CSS for fonts used inside `root`, with files inlined as data
 * URLs so a report thumbnail can draw them.
 * Returns an empty string when nothing embeddable matches.
 */
export const buildReportThumbnailFontEmbedCss = async (
  root: HTMLElement,
  options: BuildReportThumbnailFontEmbedCssOptions = {},
): Promise<string> => {
  const used = collectUsedFontFamilies(root);
  if (used.size === 0) return '';

  const fetchText = options.fetchText ?? defaultFetchText;
  const fetchDataUrl = options.fetchDataUrl ?? defaultFetchDataUrl;
  const sheets = options.styleSheets ?? defaultStyleSheets();
  const collected = await Promise.all(
    sheets.map((sheet) => readFacesFromSheet(sheet, fetchText)),
  );

  const matching = collected.flatMap(({ href, faces }) =>
    faces.flatMap((face) => {
      const family = readFontFaceFamily(face);
      if (!family || !used.has(family)) return [];
      return [{ href, face }];
    }),
  );

  const dataUrlCache = new Map<string, Promise<string>>();
  const cachedFetchDataUrl = (url: string): Promise<string> => {
    const pending = dataUrlCache.get(url);
    if (pending) return pending;
    const next = fetchDataUrl(url);
    dataUrlCache.set(url, next);
    return next;
  };

  const embedded = await Promise.all(
    matching.map(({ href, face }) =>
      embedFontFace(face, href, cachedFetchDataUrl),
    ),
  );

  const faces: string[] = [];
  for (const face of embedded) {
    if (!face) continue;
    faces.push(face);
  }
  return faces.join('\n');
};
